'use client';

import React from 'react';
import Image, { ImageProps } from 'next/image';
import { getOptimizedImageProps, ImageConfig } from '@/lib/imageUtils';

interface OptimizedImageProps extends Omit<ImageProps, 'src'> {
  src: string;
  alt: string;
  imageType?: keyof typeof ImageConfig;
  blurPreview?: boolean;
}

/**
 * OptimizedImage component that wraps Next.js Image component with optimized settings
 * 
 * @example
 * // Basic usage
 * <OptimizedImage 
 *   src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/image.jpg" 
 *   alt="Description"
 * />
 * 
 * // With specific type and custom props
 * <OptimizedImage 
 *   src="https://cdn.shopify.com/s/files/1/0675/1328/6842/files/hero.jpg" 
 *   alt="Hero Image"
 *   imageType="hero"
 *   fill
 *   className="object-cover rounded-md"
 * />
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  imageType = 'decorative',
  blurPreview = false,
  ...props
}) => {
  // Special handling for animated GIFs
  if (typeof src === 'string' && src.endsWith('.gif')) {
    return <Image 
      src={src} 
      alt={alt} 
      unoptimized 
      {...props} 
    />;
  }

  // For non-GIF images, apply our optimization settings
  const finalProps: ImageProps = {
    src: src,
    alt: alt,
    ...props
  };

  // Add type-specific configs
  if (imageType === 'hero') {
    finalProps.priority = true;
    finalProps.quality = 85;
    finalProps.sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';
  } else if (imageType === 'product') {
    finalProps.quality = 85;
    finalProps.sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 30vw, 30vw';
    finalProps.loading = 'lazy';
  } else if (imageType === 'thumbnail') {
    finalProps.quality = 70;
    finalProps.sizes = '(max-width: 640px) 50vw, 20vw';
    finalProps.loading = 'lazy';
  } else {
    // Default decorative
    finalProps.quality = 75;
    finalProps.loading = 'lazy';
  }

  // Add blur placeholder if requested
  if (blurPreview) {
    finalProps.placeholder = 'blur';
    finalProps.blurDataURL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
  }

  return <Image {...finalProps} />;
};

export default OptimizedImage; 